import { useEffect, useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input, Label } from '@pantry/ui';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { QrCodeIcon } from '@heroicons/react/24/solid';
import {
  useProductSearchWithDebounce,
  type LocalProductResult,
  type ExternalProductResult,
  type Product,
  type ItemUnit,
} from '@pantry/api-client';
import type { SelectedProduct } from '@pantry/shared';
import { WebBarcodeScanner } from '../../barcode/WebBarcodeScanner';
import { TranslationIndicator } from '../../products/TranslationIndicator';
import { ProductInternationalNames } from '../../products/ProductInternationalNames';
import { useProductName, useResolveProductName } from '../../../hooks/useProductLanguage';

interface ProductSearchSectionProps {
  householdId: string;
  selectedProduct: SelectedProduct;
  onProductSelect: (product: SelectedProduct) => void;
  customName: string;
  onCustomNameChange: (name: string) => void;
  defaultProductSearch?: string;
  allowExternalProducts?: boolean;
  onQuantityChange?: (q: number) => void;
  onUnitChange?: (unit: ItemUnit) => void;
}

/**
 * Product search section for the unified item form.
 * Handles product search input, barcode scanning, results dropdown,
 * selected product card, and translation components.
 */
export function ProductSearchSection({
  householdId,
  selectedProduct,
  onProductSelect,
  customName: _customName,
  onCustomNameChange,
  defaultProductSearch,
  allowExternalProducts,
  onQuantityChange,
  onUnitChange,
}: ProductSearchSectionProps) {
  const { t } = useLingui();
  const resolveProductName = useResolveProductName();
  const [showProductSearch, setShowProductSearch] = useState(!!defaultProductSearch);
  const [showScanner, setShowScanner] = useState(false);
  const [barcodeQuery, setBarcodeQuery] = useState('');

  const {
    searchTerm,
    setSearchTerm,
    isBarcode: isBarcodeLikeInput,
    results: searchResults,
    isLoading: productsFetching,
  } = useProductSearchWithDebounce({
    householdId,
    barcodeOverride: barcodeQuery || undefined,
  });

  // Initialize search term from default if provided
  useEffect(() => {
    if (defaultProductSearch) {
      setSearchTerm(defaultProductSearch);
    }
  }, [defaultProductSearch, setSearchTerm]);

  const handleSelectLocalProduct = (product: LocalProductResult) => {
    onProductSelect({ type: 'local', product });
    const name = resolveProductName(product);
    setSearchTerm(name);
    setBarcodeQuery('');
    setShowProductSearch(false);
    onCustomNameChange(name);
    if (product.defaultQuantity && onQuantityChange) onQuantityChange(product.defaultQuantity);
    if (product.defaultUnit && onUnitChange) onUnitChange(product.defaultUnit);
  };

  const handleSelectExternalProduct = (product: ExternalProductResult) => {
    onProductSelect({ type: 'external', product });
    const name = resolveProductName(product);
    setSearchTerm(name);
    setBarcodeQuery('');
    setShowProductSearch(false);
    onCustomNameChange(name);
    if (product.defaultQuantity && onQuantityChange) onQuantityChange(product.defaultQuantity);
    if (product.defaultUnit && onUnitChange) onUnitChange(product.defaultUnit);
  };

  const handleClearProduct = () => {
    onProductSelect(null);
    setSearchTerm('');
    setBarcodeQuery('');
  };

  const handleBarcodeScan = (barcode: string) => {
    setShowScanner(false);
    setBarcodeQuery(barcode);
    setSearchTerm('');
    setShowProductSearch(true);
  };

  // Build product for translation components (local and external products)
  const translationProduct = useMemo(() => {
    if (!selectedProduct) return null;
    if (selectedProduct.type === 'local') {
      return selectedProduct.product as unknown as Product;
    }
    // External products have names data but no id - cast with empty id
    // ProductInternationalNames will materialize on first edit
    return {
      ...selectedProduct.product,
      id: '',
    } as unknown as Product;
  }, [selectedProduct]);
  const resolvedProductName = useProductName(translationProduct);

  const hasLocalResults = searchResults?.local && searchResults.local.length > 0;
  const hasExternalResults = searchResults?.external && searchResults.external.length > 0;
  const hasAnyResults = hasLocalResults || hasExternalResults;

  return (
    <>
      <div className="space-y-2" data-tour="inventory-form-product">
        <Label htmlFor="product">
          <Trans>Product</Trans>
        </Label>
        {selectedProduct ? (
          <div className="space-y-2">
            <div className="flex items-center gap-3 rounded-xl border border-primary bg-primary/5 p-3 animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium">
                    {resolvedProductName || selectedProduct.product.name}
                  </p>
                  {selectedProduct.type === 'external' && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      <GlobeAltIcon className="h-3 w-3" />
                      <Trans>Open Food Facts</Trans>
                    </span>
                  )}
                  {selectedProduct.type === 'local' && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-fresh/10 px-2 py-0.5 text-xs text-fresh dark:bg-fresh/20">
                      <CheckCircleIcon className="h-3 w-3" />
                      <Trans>Saved</Trans>
                    </span>
                  )}
                </div>
                {selectedProduct.product.brand && (
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {selectedProduct.product.brand}
                  </p>
                )}
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={handleClearProduct}>
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </div>

            {/* Translation indicator - show for any local product */}
            {translationProduct && resolvedProductName && (
              <TranslationIndicator
                product={translationProduct}
                currentName={resolvedProductName}
                variant="inline"
              />
            )}
            {/* International names */}
            {translationProduct && (
              <ProductInternationalNames
                product={translationProduct}
                variant="inline"
                onProductMaterialized={(materializedProduct) => {
                  onProductSelect({
                    type: 'local',
                    product: materializedProduct as unknown as LocalProductResult,
                  });
                }}
              />
            )}
          </div>
        ) : (
          <div className="relative">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="product"
                  type="text"
                  placeholder={t`Search products...`}
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setBarcodeQuery('');
                    setShowProductSearch(true);
                  }}
                  onFocus={() => setShowProductSearch(true)}
                />
                {productsFetching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setShowScanner(true)}
                title={t`Scan barcode`}
              >
                <QrCodeIcon className="h-5 w-5" />
              </Button>
            </div>

            {/* Barcode search indicator */}
            {barcodeQuery && (
              <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                <QrCodeIcon className="h-4 w-4" />
                <span>
                  <Trans>Searching for barcode: {barcodeQuery}</Trans>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2"
                  onClick={() => setBarcodeQuery('')}
                >
                  <Trans>Clear</Trans>
                </Button>
              </div>
            )}

            {/* Search Results Dropdown */}
            {showProductSearch &&
              (searchTerm.length >= 2 || isBarcodeLikeInput || barcodeQuery) &&
              hasAnyResults && (
                <div className="absolute z-10 mt-2 w-full rounded-xl border border-border bg-card shadow-lg max-h-60 overflow-y-auto overflow-x-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  {/* Local Products Section */}
                  {hasLocalResults && (
                    <>
                      <div className="px-3 py-2 bg-muted/50 border-b border-border sticky top-0">
                        <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <CheckCircleIcon className="h-3 w-3" />
                          <Trans>Your Products</Trans>
                        </span>
                      </div>
                      {searchResults!.local.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => handleSelectLocalProduct(product)}
                          className="w-full px-3 py-2.5 text-left hover:bg-primary/5 transition-colors duration-150"
                        >
                          <p className="font-medium">{resolveProductName(product)}</p>
                          {product.brand && (
                            <p className="text-sm text-muted-foreground">{product.brand}</p>
                          )}
                        </button>
                      ))}
                    </>
                  )}

                  {/* External Products Section */}
                  {allowExternalProducts !== false && hasExternalResults && (
                    <>
                      <div className="px-3 py-2 bg-muted/50 border-b border-border sticky top-0">
                        <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <GlobeAltIcon className="h-3 w-3" />
                          <Trans>Open Food Facts</Trans>
                        </span>
                      </div>
                      {searchResults!.external.map((product) => (
                        <button
                          key={product.barcode}
                          type="button"
                          onClick={() => handleSelectExternalProduct(product)}
                          className="w-full px-3 py-2.5 text-left hover:bg-primary/5 transition-colors duration-150"
                        >
                          <p className="font-medium">{resolveProductName(product)}</p>
                          {product.brand && (
                            <p className="text-sm text-muted-foreground">{product.brand}</p>
                          )}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}

            {/* No results message for barcode search */}
            {showProductSearch &&
              (barcodeQuery || isBarcodeLikeInput) &&
              !productsFetching &&
              !hasAnyResults && (
                <div className="absolute z-10 mt-2 w-full rounded-xl border border-border bg-card shadow-lg p-4 text-center animate-in fade-in slide-in-from-top-2 duration-200">
                  <p className="text-sm text-muted-foreground">
                    <Trans>No product found for this barcode</Trans>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <Trans>Enter a custom name below to add it manually</Trans>
                  </p>
                </div>
              )}
          </div>
        )}
      </div>

      {/* Barcode Scanner Modal */}
      {showScanner && (
        <WebBarcodeScanner onScan={handleBarcodeScan} onClose={() => setShowScanner(false)} />
      )}
    </>
  );
}
